import { useQueryClient } from '@tanstack/react-query';
import { AttributeApiModel } from '../../../../types/models/attribute.model';
import { NodeApiModel } from '../../../../types/models/node.model';
import { AddAttachmentToNodeApiRequest } from '../../../../types/requests/ontology/attachments/add-attachment-to-node.request';
import { GetAllAttachmentsForNodeRequest } from '../../../../types/requests/ontology/attachments/get-all-attachments-for-node.request';
import { RemoveAttachmentFromNodeApiRequest } from '../../../../types/requests/ontology/attachments/remove-attachment-from-node.request';
import { CreateNodeApiRequest } from '../../../../types/requests/ontology/create-node.request';
import { DeleteNodeApiRequest } from '../../../../types/requests/ontology/delete-node.request';
import { GetNodeApiRequest } from '../../../../types/requests/ontology/get-node.request';
import { SearchNodeAttributesApiRequest } from '../../../../types/requests/ontology/attributes/search-node-attributes.request';
import { SearchOntologyApiRequest } from '../../../../types/requests/ontology/search-ontology.request';
import { UpdateNodeApiRequest } from '../../../../types/requests/ontology/update-node.request';
import {
  addAttachmentToNode,
  addDomainToNode,
  addDomainToNodeAttribute,
  createNode,
  createNodeAttribute,
  deleteNode,
  deleteNodeAttribute,
  getAllAttachmentsForNode,
  getNode,
  getNodeAttribute,
  removeAttachmentFromNode,
  removeDomainFromNode,
  removeDomainFromNodeAttribute,
  searchNodeAttributes,
  searchOntology,
  updateNode,
  updateNodeAttribute,
} from '../../api/ontology.api';
import { DeleteNodeAttributeApiRequest } from '../../../../types/requests/ontology/attributes/delete-node-attribute.request';
import { CreateNodeAttributeApiRequest } from '../../../../types/requests/ontology/attributes/create-node-attribute.request';
import { UpdateNodeAttributeApiRequest } from '../../../../types/requests/ontology/attributes/update-node-attribute.request';
import { GetNodeAttributeApiRequest } from '../../../../types/requests/ontology/attributes/get-node-attribute.request';
import { AddDomainToNodeApiRequest } from '../../../../types/requests/ontology/add-domain-to-node.request';
import { RemoveDomainFromNodeApiRequest } from '../../../../types/requests/ontology/remove-domain-from-node.request';
import { AddDomainToAttributeApiRequest } from '../../../../types/requests/ontology/attributes/add-domain-to-attribute.request';
import { RemoveDomainFromAttributeApiRequest } from '../../../../types/requests/ontology/attributes/remove-domain-from-attribute.request';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'node';
const searchQueryKey = 'search';
const baseAttributeQueryKey = 'attribute';

export const useGetNode = <T extends NodeApiModel>(params: GetNodeApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getNode<T>(params)).data,
    enabled: Boolean(params.nodeId && !Number.isNaN(params.nodeId) && params.nodeType),
  });
  return { ...result, node: data } as const;
};;

export const useSearchOntology = (params: SearchOntologyApiRequest, enabled?: boolean) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchOntology({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
    enabled,
  });
  return { ...result, response: data } as const;
};

export const useCreateNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateNodeApiRequest) => (await createNode(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, node: data }] as const;
};

export const useUpdateNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateNodeApiRequest) => (await updateNode(params)).data,
    onSuccess: (node) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetNodeApiRequest)?.nodeId === node.id),
      }),
  });
  return [request, { ...result, node: data }] as const;
};

export const useDeleteNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteNodeApiRequest) => (await deleteNode(params)).data,
    onSuccess: (_, { nodeId }) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetNodeApiRequest)?.nodeId === nodeId),
      }),
  });
  return [request, result] as const;
};

export const useCreateNodeAttribute = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateNodeAttributeApiRequest) => (await createNodeAttribute(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseAttributeQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, attribute: data }] as const;
};

export const useUpdateNodeAttribute = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateNodeAttributeApiRequest) => (await updateNodeAttribute(params)).data,
    onSuccess: (attribute) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseAttributeQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetNodeAttributeApiRequest)?.attributeId === attribute.id),
      }),
  });
  return [request, { ...result, attribute: data }] as const;
};

export const useGetNodeAttribute = <T extends AttributeApiModel>(params: GetNodeAttributeApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseAttributeQueryKey, { attributeId: params.attributeId, nodeId: params.nodeId }],
    queryFn: async () => (await getNodeAttribute<T>(params)).data,
    enabled: Boolean(
      params.nodeId && !Number.isNaN(params.nodeId) && params.attributeId && !Number.isNaN(params.attributeId),
    ),
  });
  return { ...result, attribute: data } as const;
};

export const useSearchNodeAttributes = <T extends AttributeApiModel>(params: SearchNodeAttributesApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseAttributeQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchNodeAttributes<T>({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteNodeAttribute = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteNodeAttributeApiRequest) => (await deleteNodeAttribute(params)).data,
    onSuccess: (_, { attributeId }) => queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === baseAttributeQueryKey &&
        (queryKey[1] === searchQueryKey || (queryKey[1] as GetNodeAttributeApiRequest)?.attributeId === attributeId),
    }),
  });
  return [request, result] as const;
};

export const useAddDomainToNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddDomainToNodeApiRequest) => (await addDomainToNode(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetNodeApiRequest)?.nodeId === params.nodeId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveDomainFromNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveDomainFromNodeApiRequest) => (await removeDomainFromNode(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetNodeApiRequest)?.nodeId === params.nodeId,
      }),
  });
  return [request, result] as const;
};

export const useAddDomainToNodeAttribute = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddDomainToAttributeApiRequest) => (await addDomainToNodeAttribute(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseAttributeQueryKey &&
          (queryKey[1] as GetNodeAttributeApiRequest)?.attributeId === params.attributeId,
      }),
  });
  return [request, result] as const;
};

export const useRemoveDomainFromNodeAttribute = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveDomainFromAttributeApiRequest) => (await removeDomainFromNodeAttribute(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseAttributeQueryKey &&
          (queryKey[1] as GetNodeAttributeApiRequest)?.attributeId === params.attributeId,
      }),
  });
  return [request, result] as const;
};

export const useAddAttachmentToNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: AddAttachmentToNodeApiRequest) => (await addAttachmentToNode(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetNodeApiRequest)?.nodeId === params.nodeId,
      }),
  });
  return [request, result] as const;
};

export const useGetAllAttachmentForNode = (params: GetAllAttachmentsForNodeRequest) => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAllAttachmentsForNode(params)).data,
    enabled: Boolean(params.nodeId && !Number.isNaN(params.nodeId) && params.nodeType),
  });
  return { ...result, attachments: data?.attachments } as const;
};

export const useRemoveAttachmentFromNode = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: RemoveAttachmentFromNodeApiRequest) => (await removeAttachmentFromNode(params)).data,
    onSuccess: (_, params) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey && (queryKey[1] as GetNodeApiRequest)?.nodeId === params.nodeId,
      }),
  });
  return [request, result] as const;
};
